import { addBreadcrumb } from '@sentry/react'

/**
 * First-party service-worker registration (KAN-433 / Sentry INTELHUB-WEB-2).
 *
 * vite-plugin-pwa's `injectRegister: 'auto'` default injected a generated
 * `registerSW.js` shim which was, verbatim:
 *
 *   if('serviceWorker' in navigator) {
 *     window.addEventListener('load', () => {
 *       navigator.serviceWorker.register('/sw.js', { scope: '/' })
 *     })
 *   }
 *
 * The registration promise got no handler, so when the browser refused
 * registration — private browsing, blocked site data, enterprise policy, an
 * extension — the rejection reached `onunhandledrejection` and Sentry reported
 * a bare, unactionable `Error: Rejected` (6 events, userCount 0).
 *
 * This module is that shim plus a `.catch()`, and `injectRegister` is now
 * `null` so the plugin no longer emits its own. It is deliberately a
 * like-for-like replacement:
 *
 *  - same URL and scope (asserted against the plugin config in
 *    src/__tests__/pwa-options.test.ts, so the two cannot drift apart);
 *  - same `window.load` timing;
 *  - no behaviour change to the update cycle. `skipWaiting` + `clientsClaim`
 *    in the generated SW continue to do the updating.
 *
 * The alternative — importing `registerSW` from `virtual:pwa-register`, which
 * carries its own `.catch()` — was rejected: it pulls in `workbox-window` as a
 * new runtime dependency, and its `autoUpdate` path adds a
 * `window.location.reload()` when a new SW activates. Production does not do
 * that today, and silently reloading a user's page mid-session is a change
 * well beyond fixing an unhandled rejection.
 *
 * Note this is error *handling*, not signal suppression: a browser declining
 * to install a service worker is an expected environmental outcome and the app
 * degrades to plain online operation with no user impact. It is recorded as a
 * breadcrumb so it still gives context on any later real error in the session.
 */

/** Must match `buildBase + filename` in src/pwa-options.ts. */
export const SERVICE_WORKER_URL = '/sw.js'
/** Must match `scope` in src/pwa-options.ts. */
export const SERVICE_WORKER_SCOPE = '/'

function describeError(error: unknown): string {
  if (error instanceof Error) return error.message || error.name
  if (typeof error === 'string') return error
  try {
    return JSON.stringify(error) ?? String(error)
  } catch {
    return String(error)
  }
}

export function handleServiceWorkerRegisterError(error: unknown): void {
  const reason = describeError(error)

  addBreadcrumb({
    category: 'serviceworker',
    level: 'warning',
    message: `Service worker registration failed: ${reason}`,
  })

  // Keeps the failure visible in local dev and in a user's own console without
  // creating a Sentry issue for an environmental condition we cannot act on.
  console.warn(
    '[pwa] Service worker registration failed; continuing without offline support.',
    error,
  )
}

function register(): void {
  navigator.serviceWorker
    .register(SERVICE_WORKER_URL, { scope: SERVICE_WORKER_SCOPE })
    .catch(handleServiceWorkerRegisterError)
}

/**
 * Registers the service worker. Safe to call once at startup.
 */
export function initServiceWorkerRegistration(): void {
  if (typeof window === 'undefined') return
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

  // The old shim always waited for `load`. Module scripts normally run before
  // it fires, but if it has already fired there is no second `load` event to
  // wait for, so register immediately rather than never.
  if (document.readyState === 'complete') {
    register()
    return
  }

  window.addEventListener('load', register, { once: true })
}
